import React, { FC, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { setHeader, setRoutes } from 'src/core/ui/shell';
import { AppConfig, container } from 'src/inversify.config';
import {
  LicenseLogSummary,
  LicenseLogSummaryStore,
  LicenseLogType,
  getLicenseLogTableHeaders,
  LicenseLogCsvReportRequest,
  LicenseLogSummaryCsvRequest,
  LicenseLogReportItemStore,
} from 'src/stores/licensing-logs-store';
import { TableModel, TableView } from 'src/core/ui/collections/table';
import { Card, Row } from 'antd';
import { Query } from 'src/core/stores/data-store';
import { formatMessage } from 'src/core/services/http.service';
import { DateRangeFilter } from 'src/common/components/table-filters/date-range-filter';
import ExportButtonWithModal from 'src/common/components/export-button-with-modal';
import i18next, { Languages } from '../../i18n';
import * as Moment from 'moment';
import { AutocompleteTextFilter } from 'src/common/components/table-filters/autocomplete-text-filter';
import { ENV } from 'src/';
import { emptyTableModel, getDateFormat } from 'src/common/utils';
import { AppConfiguration } from 'src/core/services/configuration.service';
import { Link } from 'react-router-dom';
import moment from 'moment';
import { DeviceType, deviceTypeLabels, getLicenseToolCode } from 'src/stores/licensing-store';

const LicenseLogsPage: FC = () => {
  const { t } = useTranslation();
  const config: AppConfiguration = useContext(AppConfig);

  const [query, setQuery] = useState({
    searchQuery: '',
    orderBy: [{ field: 'CreatedOn', direction: 'Descending', useProfile: false }],
    skip: 0,
    take: 10,
  } as Query);

  const licenseLogsStore = container.get(LicenseLogSummaryStore);
  const licenseLogsState = licenseLogsStore.state;

  const licenseLogsReportStore = container.get(LicenseLogReportItemStore);

  setHeader(t('LicenseLogs'));
  setRoutes([
    { path: '', breadcrumbName: t('Home') },
    { path: 'licenses', breadcrumbName: t('Licenses') },
    { path: 'logs', breadcrumbName: t('Logs') },
  ]);

  useEffect(
    () => {
      licenseLogsStore.load(query);
    },
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const getDescription = (data: LicenseLogSummary) => {
    const description =
      data.description === data.logType || data.logType.startsWith('ErrorCreatingLicense')
        ? `${data.description}Description`
        : `${data.logType}DescriptionExtraData`;
    switch (data.logType) {
      case LicenseLogType.LicenseCreation:
        return t(description)
          .replace('{0}', data.userName)
          .replace('{1}', deviceTypeLabels[data.deviceType as DeviceType])
          .replace('{2}', getLicenseToolCode(data))
          .replace(
            '{3}',
            data.unlimited
              ? t('Unlimited')
              : t('LicenseDescriptionTimeData')
                  .replace('{0}', `${moment(data.fromDate).utc().locale(i18next.language).format(getDateFormat())}`)
                  .replace('{1}', `${moment(data.untilDate).utc().locale(i18next.language).format(getDateFormat())}`)
          )
          .replace('{4}', data.project || '-')
          .replace('{5}', data.customer || '-');
      case LicenseLogType.LicenseUpdate:
        return t(description)
          .replace('{0}', data.userName)
          .replace('{1}', deviceTypeLabels[data.deviceType as DeviceType])
          .replace('{2}', getLicenseToolCode(data))
          .replace(
            '{3}',
            data.unlimited
              ? t('Unlimited')
              : t('LicenseDescriptionTimeData')
                  .replace('{0}', `${moment(data.fromDate).utc().locale(i18next.language).format(getDateFormat())}`)
                  .replace('{1}', `${moment(data.untilDate).utc().locale(i18next.language).format(getDateFormat())}`)
          )
          .replace('{4}', data.project || '-')
          .replace('{5}', data.customer || '-')
          .replace('{6}', deviceTypeLabels[data.newDeviceType as DeviceType])
          .replace('{7}', data.deviceType === DeviceType.SCTD ? data.newToolCode.slice(1) : data.newToolCode)
          .replace(
            '{8}',
            data.newUnlimited
              ? t('Unlimited')
              : t('LicenseDescriptionTimeData')
                  .replace('{0}', `${moment(data.newFromDate).utc().locale(i18next.language).format(getDateFormat())}`)
                  .replace('{1}', `${moment(data.newUntilDate).utc().locale(i18next.language).format(getDateFormat())}`)
          )
          .replace('{9}', data.newProject || '-')
          .replace('{10}', data.newCustomer || '-');
      case LicenseLogType.LicenseRejected:
        return t(description)
          .replace('{0}', data.userName)
          .replace('{1}', data.licenseCreatedByName)
          .replace('{2}', deviceTypeLabels[data.deviceType as DeviceType])
          .replace('{3}', getLicenseToolCode(data));
      case LicenseLogType.LicenseSignedOff:
        return t(description)
          .replace('{0}', data.userName)
          .replace('{1}', data.licenseCreatedByName)
          .replace('{2}', deviceTypeLabels[data.deviceType as DeviceType])
          .replace('{3}', getLicenseToolCode(data));
      default:
        return t(description);
    }
  };

  const getTableDescription = (description: string) => {
    const lines = description.split('<br>');
    if (lines.length === 1) {
      return <>{description}</>;
    } else {
      return lines.map((line, i) => (
        <>
          {line}
          {i <= lines.length - 1 ? <br /> : ''}
        </>
      ));
    }
  };

  const getCsvDescription = (description: string) => {
    return `"${description.replaceAll('<br>', '')}"`;
  };

  const itemTemplate: LicenseLogCsvReportRequest = {
    headers: getLicenseLogTableHeaders(t),
    csvSeparator: ',',
    currentLanguage: i18next.language !== Languages.Russian ? i18next.language : 'ru-RU',
    fileName: `license_logs.csv`,
    items: [],
  };

  const handleExportOk = async (separator: string) => {
    let tempItemModal = itemTemplate;
    tempItemModal.csvSeparator = separator || tempItemModal.csvSeparator;
    tempItemModal.items = licenseLogsState.items.value.map((x) => {
      return {
        createdOn: x.createdOn,
        logType: t(x.logType),
        userName: x.userName,
        description: getCsvDescription(getDescription(x)),
      } as LicenseLogSummaryCsvRequest;
    });

    let response = await licenseLogsReportStore.get(tempItemModal);
    console.log(response);
  };

  const tableModel = {
    query,
    columns: [
      {
        sortable: true,
        filter: <DateRangeFilter />,
        fiterField: 'createdOn',
        field: 'createdOn',
        title: t('CreatedOn'),
        width: '15%',
        renderer: (data) => <span>{Moment.utc(data.createdOn).local().locale(i18next.language).format(`${getDateFormat()}, hh:mm a`)}</span>,
      },
      {
        sortable: true,
        sortField: 'logType',
        field: 'logType',
        title: t('LogType'),
        width: '15%',
        renderer: (data) => <span>{t(data.logType)}</span>,
      },
      {
        searchable: true,
        applyPartialFilter: true,
        filter: <AutocompleteTextFilter baseUrl={licenseLogsStore.baseUrl} fieldName={'userName'} fieldValue={'userName'} />,
        sortable: true,
        sortField: 'userName',
        field: 'userName',
        title: t('User'),
        width: '15%',
        renderer: (data) => <span>{data.userName}</span>,
      },
      {
        searchable: true,
        field: 'description',
        title: t('Description'),
        renderer: (data) => <span>{getTableDescription(getDescription(data))}</span>,
      },
      {
        searchable: false,
        field: 'description',
        renderer: (data) => (data.licenseDataId ? <Link to={`/licenses/${data.licenseDataId}`}>{t('GoToLicense')}</Link> : <></>),
      },
    ],
    data: { ...licenseLogsState.value, isBusy: licenseLogsState.value.isBusy },
  } as TableModel<LicenseLogSummary>;

  return (
    <Row align="top" justify="space-between">
      <Card className="card-table" style={{ width: '100%' }}>
        <TableView
          rowKey="id"
          canCreateNew={false}
          canDelete={false}
          canEdit={false}
          canSort
          onQueryChanged={(query: Query) => {
            setQuery(query);
            licenseLogsStore.load(query);
          }}
          onRefresh={() => licenseLogsStore.load(query)}
          model={licenseLogsState.errorMessage.value ? emptyTableModel : tableModel}
          error={
            licenseLogsState.errorMessage.value &&
            (config.environment === ENV.DEV ? formatMessage(licenseLogsState.errorMessage.value) : t('LicenseLogsTableError'))
          }
          rightToolbar={
            <li>
              <ExportButtonWithModal onExport={handleExportOk} text={t('ExportCSV')} />
            </li>
          }
        />
      </Card>
    </Row>
  );
};

export default LicenseLogsPage;
